/* istanbul ignore file */
import { InspectionArea, InspectionItem, InspectionItemType, InspectionSheet } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import {
  CoverThicknessAggregate,
  Project,
  ProjectSheetItemsOrder,
  ProjectSheetRowVisibility,
  ProjectSheetSettings,
  ProjectSheetToleranceType,
} from '@editor/interfaces/project'
import { PlaneSide, Shapes } from '@editor/interfaces/shape'
import {
  DEFAULT_TOLERANCE_TYPE,
  GRID_UNIT,
  GRID_UNIT_PRECISION,
} from '@editor/pages/projects/inspection-sheet/constants'
import { ValuePassingThreshold } from '@editor/pages/projects/inspection-sheet/interfaces'
import {
  canBeEvaluated,
  getDifferenceValueAndIsPassingThresholds,
  getSelectedCoverThickness,
} from '@editor/pages/projects/inspection-sheet/utils'
import { parseInspectionItemPartitionKey } from '@editor/services/InspectionArea'
import { findInspectionAreaSheet } from '@editor/services/InspectionSheet'
import {
  isValueDefined,
  meterRounded,
  meterToMilimeterRounded,
  meterToMillimeter,
  zeroPad,
} from '@editor/services/Util'
import { HATSULY_URL } from '@infraloop/services'
import dayjs from 'dayjs'
import { Style, Workbook, Worksheet } from 'exceljs'

import {
  DATA_SHEET_NAME,
  DIAGRAM_SHEET_NAME,
  GRID_VALUES_SHEET_NAME,
  createOuterBorder,
  fitColumns,
  isDiagramShown,
  makeInternalLinkValue,
  setHeaderCell,
  setProfileGroup,
  setSummary,
  setTitle,
} from './common'
import {
  centerCenterStyle,
  nameStyle,
  nameStyleCenter,
  plusMinusValueStyleMeter,
  plusMinusValueStyleMili,
  underlineStyle,
  valueFailed,
  valueStyleMeter,
  valueStyleMili,
} from './styles'

const DATA_COLUMNS: [ProjectSheetRowVisibility, [string, string]][] = [
  ['polygon_length_1', ['辺の長さ1', '(mm)']],
  ['polygon_length_2', ['辺の長さ2', '(mm)']],
  ['polygon_perimeter', ['外周長', '(mm)']],
  ['polygon_area', ['面積', '(m²)']],
  ['volume', ['体積', '(m³)']],
  ['grid', ['深さ', '(mm)']],
  ['upper_plane_to_cylinders_distance', ['表かぶり厚', '(mm)']],
  ['lower_plane_to_cylinders_distance', ['裏かぶり厚', '(mm)']],
]

/**
 * Counts the maximum number of edge lengths for any single polygon
 *
 * @param inspectionItems All inspection items
 * @returns The maximum edge count across all polygons
 */
const getMaxEdgeCount = (inspectionItems: InspectionItem[]): number => {
  const edgesByPolygon = new Map<string, number>()

  Object.values(inspectionItems).forEach((item) => {
    if (item.item_type === InspectionItemType.EdgeLength && item.shape_ids?.polygons?.[0]) {
      const polygonId = item.shape_ids.polygons[0]
      edgesByPolygon.set(polygonId, (edgesByPolygon.get(polygonId) || 0) + 1)
    }
  })

  return edgesByPolygon.size ? Math.max(...edgesByPolygon.values()) : 0
}

/**
 * Filter the data columns based on the project settings.
 *
 * @param settings Project settings
 * @param inspectionItems All inspection items
 * @returns Filtered data columns
 */
const filterDataColumns = (settings: ProjectSheetSettings, inspectionItems: InspectionItem[]) => {
  const columns: [ProjectSheetRowVisibility, [string, string]][] = []

  // Polyline gets its own dedicated column block ('延長' parent) rather than reusing the
  // plane's 辺の長さ1/2 columns, and it comes first — before the plane columns. The block
  // holds the 延長 length (full threshold set) plus a single 幅 (width) column, added in
  // setTableHeaders.
  if (settings.sheet_models_visibility?.polyline) {
    columns.push(['polyline_length', ['延長', '(mm)']])
  }

  // Custom measurement ('距離') also gets its own dedicated column block rather than reusing
  // the plane's 辺の長さ1 column, laid out right after 延長 and before the plane columns.
  if (settings.sheet_models_visibility?.length_with_distance_tool) {
    columns.push(['length_with_distance_tool', ['距離', '(mm)']])
  }

  columns.push(...DATA_COLUMNS.filter(([key]) => settings.sheet_rows_visibility?.[key] ?? false))

  if (settings.sheet_rows_visibility?.edge_length) {
    const maxEdgeCount = getMaxEdgeCount(inspectionItems)

    for (let i = 0; i < maxEdgeCount; i += 1) {
      columns.push(['edge_length', [`頂点間の長さ L${i + 1}`, '(mm)']])
    }
  }

  return columns
}

/**
 * Set the headers for the volume table.
 *
 * @param sheet Worksheet
 * @param bodyRow Current row
 * @param settings Project settings
 * @param forProject If the row is for the project
 * @returns [New body row, number of columns per exported metric, total columns]
 */
const setTableHeaders = (
  sheet: Worksheet,
  bodyRow: number,
  settings: ProjectSheetSettings,
  inspectionItems: InspectionItem[],
  forProject = false,
): [number, [ProjectSheetRowVisibility, number][], number] => {
  let bodyCol = 1

  // inspection area
  if (forProject) {
    // name
    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, 'エリア名', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    // props
    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '工種名', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '種別', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '作成者', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '作成日', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '立会者', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1

    sheet.mergeCells(bodyRow, bodyCol, bodyRow + 2, bodyCol)
    setHeaderCell(sheet, bodyRow, bodyCol, '立会日', 'lg')
    createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol, bodyRow + 2])
    bodyCol += 1
  }

  // item group
  sheet.mergeCells(bodyRow, bodyCol, bodyRow + 1, bodyCol + 2)
  setHeaderCell(sheet, bodyRow, bodyCol, '測点', 'lg')

  // item name and type
  setHeaderCell(sheet, bodyRow + 2, bodyCol, '検査グループ')
  setHeaderCell(sheet, bodyRow + 2, bodyCol + 1, '名称')
  setHeaderCell(sheet, bodyRow + 2, bodyCol + 2, '種類')

  createOuterBorder(sheet, [bodyCol, bodyRow], [bodyCol + 2, bodyRow + 2])
  bodyCol += 3

  // // Add data columns
  let curCol = bodyCol // the current column
  let startCol = 0 // the starting column for each metric
  const colCountPerMetric: [ProjectSheetRowVisibility, number][] = []
  filterDataColumns(settings, inspectionItems).forEach(([row, [name, unit]]) => {
    startCol = curCol
    let colCount: [ProjectSheetRowVisibility, number] = [row, 0]

    // ## data values
    // actual
    if (settings.sheet_cols_visibility?.estimated_value) {
      setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '実測値')
      colCount = [row, colCount[1] + 1]
    }

    // design
    if (settings.sheet_cols_visibility?.designed_value) {
      setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '設計値')
      colCount = [row, colCount[1] + 1]
    }

    // tolerance
    if (settings.sheet_cols_visibility?.tolerance) {
      setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '規格値')
      colCount = [row, colCount[1] + 1]
    }

    // diff
    if (settings.sheet_cols_visibility?.designed_value) {
      setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '差')
      colCount = [row, colCount[1] + 1]
    }

    if (row === 'grid') {
      if (settings.sheet_rows_visibility?.min_grid_depth) {
        setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '最小')
        colCount = [row, colCount[1] + 1]
      }

      if (settings.sheet_rows_visibility?.max_grid_depth) {
        setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '最大')
        colCount = [row, colCount[1] + 1]
      }

      // link to grid values
      if (settings.sheet_diagram_visibility?.grid_diagram) {
        setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '')
        sheet.getColumn(curCol + colCount[1]).width = 15
        sheet.getColumn(curCol + colCount[1]).alignment = { vertical: 'middle', horizontal: 'center' }
        colCount = [row, colCount[1] + 1]
      }
    }

    // polyline width ('幅') is a single extra column under the shared '延長' parent
    if (row === 'polyline_length') {
      setHeaderCell(sheet, bodyRow + 2, curCol + colCount[1], '幅')
      colCount = [row, colCount[1] + 1]
    }

    colCountPerMetric.push(colCount)

    curCol += colCount[1]

    // data type
    sheet.mergeCells(bodyRow, startCol, bodyRow, curCol - 1)
    setHeaderCell(sheet, bodyRow, startCol, name, 'lg')

    // data unit
    sheet.mergeCells(bodyRow + 1, startCol, bodyRow + 1, curCol - 1)
    setHeaderCell(sheet, bodyRow + 1, startCol, unit, 'lg')

    createOuterBorder(sheet, [startCol, bodyRow], [curCol - 1, bodyRow + 2])
  })

  return [bodyRow + 3, colCountPerMetric, curCol]
}

const getStartColForCategory = (
  colCountPerMetric: [ProjectSheetRowVisibility, number][],
  dataStartCol: number,
  key: string,
) => {
  const index = colCountPerMetric.findIndex(([k]) => k === key)
  const startCol =
    index !== -1
      ? colCountPerMetric.slice(0, index).reduce((sum, [, count]) => sum + count, dataStartCol)
      : dataStartCol
  return startCol
}

/**
 * Sets the standard values for an inspection item.
 * - Design value, actual value, difference, and tolerance.
 *
 * @param sheet Worksheet
 * @param bodyRow Current row
 * @param bodyCol Current column
 * @param measuredValue Actual measured value
 * @param inspectionItem Inspection item
 * @param valueConverter Optional value converter
 * @param style Optional style
 * @param plusMinusStyle Optional style for plus/minus values
 * @returns New body column
 */
const setCategoryValues = (
  sheet: Worksheet,
  bodyRow: number,
  bodyCol: number,
  measuredValue: number | undefined | null,
  inspectionItem: InspectionItem | undefined,
  settings: ProjectSheetSettings,
  toleranceType: ProjectSheetToleranceType,
  valueConverter?: (value: number) => number,
  style?: Style,
  plusMinusStyle?: Style,

  /**
   * Extra data points to be displayed.
   */
  additionalMetrics: {
    title: string
    value: number | undefined | null
    isHidden: boolean
  }[] = [],

  /**
   * Optional link column.
   * Used to link to another cell in the same or different sheet.
   *
   * (Can be extended to a URL link in the future)
   */
  link:
    | {
        /**
         * Name of the link.
         */
        name: string

        /**
         * Sheet name to link to.
         */
        sheetName: string

        /**
         * ID of the cell to link to.
         */
        cellId: string
      }
    | undefined = undefined,
): number => {
  let validation: ValuePassingThreshold | undefined
  let evaluation: ReturnType<typeof canBeEvaluated> | undefined
  let colNum = bodyCol

  if (inspectionItem) {
    validation = getDifferenceValueAndIsPassingThresholds(
      {
        estimated_value: measuredValue,
      },
      inspectionItem.pre_defined_thresholds,
      settings.sheet_rows_tolerance_type?.grid || DEFAULT_TOLERANCE_TYPE,
      GRID_UNIT,
      GRID_UNIT_PRECISION,
    )
    evaluation = canBeEvaluated(
      settings,
      settings.sheet_rows_tolerance_type?.grid || DEFAULT_TOLERANCE_TYPE,
      inspectionItem.pre_defined_thresholds,
    )
  }

  const { differenceValue, isPassingThresholds } = validation || {}
  const { isEvaluated, isUpperToleranceDefined, isLowerToleranceDefined } = evaluation || {}

  const isFailingThresholds = (isEvaluated && !isPassingThresholds) || false
  let updatedStyles = { ...style }
  if (isFailingThresholds) {
    updatedStyles = { ...updatedStyles, ...valueFailed }
  }

  // actual value
  if (settings.sheet_cols_visibility?.estimated_value) {
    if (measuredValue) {
      sheet.getCell(bodyRow, colNum).value = valueConverter ? valueConverter(measuredValue) : measuredValue
      if (style) {
        sheet.getCell(bodyRow, colNum).style = { ...style } // exceljs will continue using the same object reference. copy to avoid overwriting.
      }
    }
    colNum += 1
  }

  // design value
  if (settings.sheet_cols_visibility?.designed_value) {
    if (inspectionItem && isValueDefined(inspectionItem.pre_defined_thresholds?.designed_value)) {
      sheet.getCell(bodyRow, colNum).value = valueConverter
        ? valueConverter(inspectionItem.pre_defined_thresholds.designed_value)
        : inspectionItem.pre_defined_thresholds.designed_value
      sheet.getCell(bodyRow, colNum).style = { ...updatedStyles }
    }
    colNum += 1
  }

  // tolerance
  if (settings.sheet_cols_visibility?.tolerance) {
    if (inspectionItem) {
      let tolerance: number | string | null | undefined =
        isValueDefined(inspectionItem.pre_defined_thresholds?.tolerance) && valueConverter
          ? valueConverter(inspectionItem.pre_defined_thresholds.tolerance)
          : inspectionItem.pre_defined_thresholds?.tolerance

      if (isPassingThresholds !== undefined) {
        if (toleranceType === ProjectSheetToleranceType.GteDesignedValue) {
          tolerance = isPassingThresholds ? '以上' : '以下'
        } else if (toleranceType === ProjectSheetToleranceType.LteDesignedValue) {
          tolerance = isPassingThresholds ? '以下' : '以上'
        }
      }

      if (isValueDefined<string | number>(tolerance)) {
        sheet.getCell(bodyRow, colNum).value = tolerance
        if (typeof tolerance === 'string')
          sheet.getCell(bodyRow, colNum).style = { ...centerCenterStyle, ...updatedStyles }
        else if (typeof tolerance === 'number')
          sheet.getCell(bodyRow, colNum).style = { ...plusMinusStyle, ...updatedStyles }
        else sheet.getCell(bodyRow, colNum).style = { ...updatedStyles }
      }

      if (isUpperToleranceDefined || isLowerToleranceDefined) {
        sheet.getCell(bodyRow, colNum).style = { ...centerCenterStyle, ...updatedStyles }

        const str = []

        if (isValueDefined(inspectionItem.pre_defined_thresholds?.lower_tolerance)) {
          const value = valueConverter
            ? valueConverter(inspectionItem.pre_defined_thresholds.lower_tolerance)
            : inspectionItem.pre_defined_thresholds?.lower_tolerance
          str.push(`-${value}`)
        }

        if (isValueDefined(inspectionItem.pre_defined_thresholds?.upper_tolerance)) {
          const value = valueConverter
            ? valueConverter(inspectionItem.pre_defined_thresholds.upper_tolerance)
            : inspectionItem.pre_defined_thresholds?.upper_tolerance
          str.push(`+${value}`)
        }

        tolerance = str.join(' ')
      }

      sheet.getCell(bodyRow, colNum).value = tolerance
    }
    colNum += 1
  }

  // difference
  if (settings.sheet_cols_visibility?.designed_value) {
    if (differenceValue !== undefined) {
      sheet.getCell(bodyRow, colNum).value = valueConverter ? valueConverter(differenceValue) : differenceValue
      sheet.getCell(bodyRow, colNum).style = { ...updatedStyles }
    }
    colNum += 1
  }

  // additional metrics — the column is reserved whenever the metric is visible (matching the
  // header), even if this row has no value for it, so later columns stay aligned.
  additionalMetrics.forEach((metric) => {
    if (!metric.isHidden) {
      if (metric.value !== undefined && metric.value !== null) {
        sheet.getCell(bodyRow, colNum).value = valueConverter ? valueConverter(metric.value) : metric.value
        if (style) sheet.getCell(bodyRow, colNum).style = { ...updatedStyles }
      }
      colNum += 1
    }
  })

  // optional link column — reserved whenever a link slot is passed (matching the header); the
  // hyperlink itself is only written when there is a target cell to link to.
  if (link) {
    if (link.cellId) {
      sheet.getCell(bodyRow, colNum).value = makeInternalLinkValue(link.sheetName, link.cellId, link.name)
      sheet.getCell(bodyRow, colNum).style = { ...centerCenterStyle, ...updatedStyles, ...underlineStyle }
    }

    colNum += 1
  }

  return colNum
}

/**
 * Set the columns for plane inspection items.
 *
 * @param sheet Worksheet
 * @param bodyCol Current column
 * @param bodyRow Current row
 * @param volume Volume inspection item
 * @param inspectionItems Inspection items related to the volume
 * @returns New body column
 */
const setPlaneColumns = (
  cellId: string,
  sheet: Worksheet,
  bodyCol: number,
  bodyRow: number,
  name: string,
  type: string,
  inspectionItems: InspectionItem[],
  inspectionGroup: InspectionGroup | undefined,
  settings: ProjectSheetSettings,

  /**
   * Number of leading columns to skip (and grey out) before the plane metrics.
   * Used to jump past the polyline ('延長') block, which is laid out first.
   */
  leadingGap = 0,
): number => {
  let updatedCol = bodyCol

  // set group name
  sheet.getCell(bodyRow, updatedCol).value = inspectionGroup?.name
  sheet.getCell(bodyRow, updatedCol).style = { ...nameStyleCenter }
  updatedCol += 1

  // set volume name
  if (isDiagramShown(settings)) {
    sheet.getCell(bodyRow, updatedCol).value = {
      formula: `HYPERLINK("#'${DIAGRAM_SHEET_NAME}'!"&ADDRESS(CELL("row", diagram_${cellId}), CELL("col",diagram_${cellId})), "${name}")`,
      date1904: false,
    }
    sheet.getCell(bodyRow, updatedCol).style = { ...nameStyleCenter, ...underlineStyle } // copy to avoid overwriting
    sheet.getCell(bodyRow, updatedCol).name = `data_${cellId}`
  } else {
    sheet.getCell(bodyRow, updatedCol).value = name
    sheet.getCell(bodyRow, updatedCol).style = { ...nameStyleCenter }
  }
  updatedCol += 1

  // set type
  sheet.getCell(bodyRow, updatedCol).value = type
  sheet.getCell(bodyRow, updatedCol).style = { ...nameStyleCenter }
  updatedCol += 1

  // Skip and grey the leading polyline ('延長') block that sits before the plane metrics.
  for (let g = 0; g < leadingGap; g += 1) {
    sheet.getCell(bodyRow, updatedCol).style = { ...nameStyle } // copy to avoid overwriting
    updatedCol += 1
  }

  // Length 1 (Polygon)
  if (settings.sheet_rows_visibility?.polygon_length_1) {
    const polyLength1 = inspectionItems.find((it) => it.item_type === InspectionItemType.PolygonLength1)
    updatedCol = setCategoryValues(
      sheet,
      bodyRow,
      updatedCol,
      polyLength1?.polygon_length_1?.estimated_value,
      polyLength1,
      settings,
      settings.sheet_rows_tolerance_type?.polygon_length_1 || DEFAULT_TOLERANCE_TYPE,
      meterToMilimeterRounded,
      valueStyleMili,
      plusMinusValueStyleMili,
    )
  }

  // Length 2 (Polygon)
  if (settings.sheet_rows_visibility?.polygon_length_2) {
    const polyLength2 = inspectionItems.find((it) => it.item_type === InspectionItemType.PolygonLength2)
    updatedCol = setCategoryValues(
      sheet,
      bodyRow,
      updatedCol,
      polyLength2?.polygon_length_2?.estimated_value,
      polyLength2,
      settings,
      settings.sheet_rows_tolerance_type?.polygon_length_2 || DEFAULT_TOLERANCE_TYPE,
      meterToMilimeterRounded,
      valueStyleMili,
      plusMinusValueStyleMili,
    )
  }

  // Perimeter (Polygon)
  if (settings.sheet_rows_visibility?.polygon_perimeter) {
    const polyPerimeter = inspectionItems.find((it) => it.item_type === InspectionItemType.PolygonPerimeter)
    updatedCol = setCategoryValues(
      sheet,
      bodyRow,
      updatedCol,
      polyPerimeter?.polygon_perimeter?.estimated_value,
      polyPerimeter,
      settings,
      settings.sheet_rows_tolerance_type?.polygon_perimeter || DEFAULT_TOLERANCE_TYPE,
      meterToMilimeterRounded,
      valueStyleMili,
      plusMinusValueStyleMili,
    )
  }

  // Area (Polygon)
  if (settings.sheet_rows_visibility?.polygon_area) {
    const polyArea = inspectionItems.find((it) => it.item_type === InspectionItemType.PolygonArea)
    updatedCol = setCategoryValues(
      sheet,
      bodyRow,
      updatedCol,
      polyArea?.polygon_area?.estimated_value,
      polyArea,
      settings,
      settings.sheet_rows_tolerance_type?.polygon_area || DEFAULT_TOLERANCE_TYPE,
      meterRounded,
      valueStyleMeter,
      plusMinusValueStyleMeter,
    )
  }

  return updatedCol
}

/**
 * Set rows for an inspection area.
 *
 * @param sheet Current worksheet
 * @param startRow Current row
 * @param colCountPerMetric Number of columns per metric
 * @param totalCols Total columns
 * @param inspectionArea Inspection Area
 * @param inspectionItems Inspection Items for the project
 * @param shapes Shapes of the project
 * @param settings Project settings
 * @param forProject If the row is for the project
 * @returns New body row
 */
const setInspectionAreaRow = (
  sheet: Worksheet,
  startRow: number,
  colCountPerMetric: [ProjectSheetRowVisibility, number][],
  totalCols: number,
  project: Project,
  inspectionArea: InspectionArea,
  inspectionSheet: InspectionSheet,
  inspectionItems: InspectionItem[],
  inspectionGroups: InspectionGroup[],
  shapes: Shapes,
  settings: ProjectSheetSettings,
  forProject = false,
): number => {
  let updatedRow = startRow
  const startingRow = updatedRow
  const beginningCol = forProject ? 1 : 0 // 1 is for area name
  const dataStartCol = forProject ? 1 + 6 : 0 // 1 is for area name, 6 is for profile group

  // Figure out how many rows will we have. It will be a total of volumes + standalone planes
  const volumes = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.Volume)
    .map((item, index) => ({
      ...item,
      part_name: item.part_name || `体積 ${zeroPad(index + 1, 3)}`,
    }))

  const standaloneShapes = inspectionItems
    .filter(
      (item) =>
        volumes.find((v) => v.inspection_item_id === item.volume_id) === undefined &&
        item.item_type === InspectionItemType.PolygonArea,
    )
    .map((item, index) => ({
      ...item,
      part_name: item.part_name || `面積 ${zeroPad(index + 1, 3)}`,
    }))
  const customMeasurement = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.LengthWithDistanceTool)
    .map((item, index) => ({
      ...item,
      part_name: item.part_name || `距離 ${zeroPad(index + 1, 3)}`,
    }))

  // Get polylines
  const polylines = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.PolylineLength)
    .map((item, index) => ({
      ...item,
      part_name: item.part_name || `延長 ${zeroPad(index + 1, 3)}`,
    }))

  // Total width of the leading line-measurement blocks ('延長' and '距離'), which are laid
  // out before the plane columns and must be skipped when writing plane metrics.
  const leadingLineCols =
    (colCountPerMetric.find(([key]) => key === 'polyline_length')?.[1] ?? 0) +
    (colCountPerMetric.find(([key]) => key === 'length_with_distance_tool')?.[1] ?? 0)

  const setRow = (inspectionItem: InspectionItem) => {
    // volume
    if (settings.sheet_models_visibility?.volume && inspectionItem.item_type === InspectionItemType.Volume) {
      let bodyCol = dataStartCol + 1
      const volumeShapes = {
        polygons:
          shapes.polygons?.filter((polygon) => inspectionItem.shape_ids.polygons?.includes(polygon.shape_id)) || [],
      }

      // Upper-plane-specific columns
      const upperPlaneId = volumeShapes.polygons.find((p) => p.plane_side === PlaneSide.UPPER)?.shape_id || ''
      bodyCol = setPlaneColumns(
        inspectionItem.inspection_item_id?.substring(inspectionItem.inspection_item_id.length - 8) || '',
        sheet,
        bodyCol,
        updatedRow,
        inspectionItem.part_name || '',
        '体積',
        inspectionItems.filter((it) => it.shape_ids.polygons?.includes(upperPlaneId)),
        inspectionGroups.find((group) => inspectionItem.inspection_group_id === group.group_id),
        settings,
        leadingLineCols,
      )

      // Volume columns
      if (settings.sheet_rows_visibility?.volume) {
        bodyCol = setCategoryValues(
          sheet,
          updatedRow,
          bodyCol,
          inspectionItem.volume?.estimated_value || 0,
          inspectionItem,
          settings,
          settings.sheet_rows_tolerance_type?.volume || DEFAULT_TOLERANCE_TYPE,
          undefined,
          valueStyleMeter,
          plusMinusValueStyleMeter,
        )
      }

      // Grid depth
      if (settings.sheet_rows_visibility?.grid) {
        const gridDepth = inspectionItems.find(
          (it) => it.item_type === InspectionItemType.Grid && it.volume_id === inspectionItem.inspection_item_id,
        )
        bodyCol = setCategoryValues(
          sheet,
          updatedRow,
          bodyCol,
          gridDepth?.grid?.estimated_value,
          gridDepth,
          settings,
          settings.sheet_rows_tolerance_type?.grid || DEFAULT_TOLERANCE_TYPE,
          meterToMillimeter,
          valueStyleMili,
          plusMinusValueStyleMili,
          // Reserve 最小/最大 columns by visibility (matching the header) regardless of whether
          // this volume has grid data, so cover-thickness/edge columns stay aligned.
          [
            {
              title: '最小',
              value: gridDepth?.grid?.min_grid_depth,
              isHidden: !settings.sheet_rows_visibility?.min_grid_depth,
            },
            {
              title: '最大',
              value: gridDepth?.grid?.max_grid_depth,
              isHidden: !settings.sheet_rows_visibility?.max_grid_depth,
            },
          ],
          // Reserve the grid-values link column whenever the grid diagram is visible; the link
          // is only populated when there is grid data to point to.
          settings.sheet_diagram_visibility?.grid_diagram
            ? {
                name: 'グリッド点',
                sheetName: GRID_VALUES_SHEET_NAME,
                cellId: gridDepth?.grid
                  ? `grid_values_${gridDepth?.inspection_item_id?.substring(gridDepth.inspection_item_id.length - 8) || ''}`
                  : '',
              }
            : undefined,
        )
      }

      // cylinder depth (upper)
      if (settings.sheet_rows_visibility?.upper_plane_to_cylinders_distance) {
        const upperDepth = inspectionItems.find(
          (item) =>
            item.item_type === InspectionItemType.PlaneToCylindersDistance &&
            shapes.polygons?.find(
              (shape) => shape.shape_id === item.shape_ids.polygons[0] && shape.plane_side === PlaneSide.UPPER,
            ) &&
            item.volume_id === inspectionItem.inspection_item_id,
        )
        bodyCol = setCategoryValues(
          sheet,
          updatedRow,
          bodyCol,
          getSelectedCoverThickness(
            upperDepth?.plane_to_cylinders_distance,
            settings.cover_thickness_value_type?.upper ?? CoverThicknessAggregate.Min,
          ),
          upperDepth,
          settings,
          settings.sheet_rows_tolerance_type?.upper_plane_to_cylinders_distance || DEFAULT_TOLERANCE_TYPE,
          meterToMillimeter,
          valueStyleMili,
          plusMinusValueStyleMili,
        )
      }

      // cylinder depth (lower)
      if (settings.sheet_rows_visibility?.lower_plane_to_cylinders_distance) {
        const lowerDepth = inspectionItems.find(
          (item) =>
            item.item_type === InspectionItemType.PlaneToCylindersDistance &&
            shapes.polygons?.find(
              (shape) => shape.shape_id === item.shape_ids.polygons[0] && shape.plane_side === PlaneSide.LOWER,
            ) &&
            item.volume_id === inspectionItem.inspection_item_id,
        )
        bodyCol = setCategoryValues(
          sheet,
          updatedRow,
          bodyCol,
          getSelectedCoverThickness(
            lowerDepth?.plane_to_cylinders_distance,
            settings.cover_thickness_value_type?.lower ?? CoverThicknessAggregate.Min,
          ),
          lowerDepth,
          settings,
          settings.sheet_rows_tolerance_type?.lower_plane_to_cylinders_distance || DEFAULT_TOLERANCE_TYPE,
          meterToMillimeter,
          valueStyleMili,
          plusMinusValueStyleMili,
        )
      }

      // Edge lengths
      if (settings.sheet_rows_visibility?.edge_length) {
        const edgeLengths = inspectionItems.filter(
          (item) =>
            item.item_type === InspectionItemType.EdgeLength &&
            item.volume_id === inspectionItem.inspection_item_id &&
            shapes.polygons?.find((shape) => shape.shape_id === item.shape_ids.polygons?.[0]),
        )

        edgeLengths.sort((a, b) => (a.edge_length?.index || 0) - (b.edge_length?.index || 0))

        const edgeLengthStartCol = getStartColForCategory(colCountPerMetric, dataStartCol + 4, 'edge_length')

        bodyCol = edgeLengthStartCol
        edgeLengths.forEach((edgeLength) => {
          bodyCol = setCategoryValues(
            sheet,
            updatedRow,
            bodyCol,
            edgeLength?.edge_length?.estimated_value,
            edgeLength,
            settings,
            settings.sheet_rows_tolerance_type?.edge_length || DEFAULT_TOLERANCE_TYPE,
            meterToMilimeterRounded,
            valueStyleMili,
            plusMinusValueStyleMili,
          )
        })
      }

      updatedRow += 1
    }

    // standalone planes
    else if (settings.sheet_models_visibility?.plane && inspectionItem.item_type === InspectionItemType.PolygonArea) {
      const polygon = shapes.polygons?.find((shape) => shape.shape_id === inspectionItem.shape_ids.polygons?.[0])
      let newColNumber = setPlaneColumns(
        inspectionItem.inspection_item_id?.substring(inspectionItem.inspection_item_id.length - 8) || '',
        sheet,
        dataStartCol + 1,
        updatedRow,
        inspectionItem.part_name || '',
        '面積',
        inspectionItems.filter((it) => it.shape_ids.polygons?.includes(inspectionItem.shape_ids.polygons[0])),
        polygon ? inspectionGroups.find((group) => polygon.inspection_group_id === group.group_id) : undefined,
        settings,
        leadingLineCols,
      )

      // no grid and volume for standalone shapes, so we fill with grey cells up until the edge
      // lengths (or the end of the table when edge lengths aren't shown).
      const edgeLengthStartCol = getStartColForCategory(colCountPerMetric, dataStartCol + 4, 'edge_length')
      const greyEndCol = settings.sheet_rows_visibility?.edge_length ? edgeLengthStartCol : totalCols
      for (let i = newColNumber; i < greyEndCol; i += 1) {
        sheet.getCell(updatedRow, i).style = { ...nameStyle } // copy to avoid overwriting
      }

      // Edge lengths
      if (settings.sheet_rows_visibility?.edge_length) {
        newColNumber = edgeLengthStartCol
        const edgeLengths = inspectionItems.filter(
          (it) =>
            it.item_type === InspectionItemType.EdgeLength &&
            it.shape_ids.polygons?.[0] === inspectionItem.shape_ids.polygons?.[0],
        )

        edgeLengths.sort((a, b) => (a.edge_length?.index || 0) - (b.edge_length?.index || 0))
        edgeLengths.forEach((edgeLength) => {
          newColNumber = setCategoryValues(
            sheet,
            updatedRow,
            newColNumber,
            edgeLength?.edge_length?.estimated_value,
            edgeLength,
            settings,
            settings.sheet_rows_tolerance_type?.edge_length || DEFAULT_TOLERANCE_TYPE,
            meterToMilimeterRounded,
            valueStyleMili,
            plusMinusValueStyleMili,
          )
        })
      }

      updatedRow += 1
    }

    // polylines
    else if (
      settings.sheet_models_visibility?.polyline &&
      inspectionItem.item_type === InspectionItemType.PolylineLength
    ) {
      // group
      const group = inspectionGroups.find((gr) => inspectionItem.inspection_group_id === gr.group_id)
      sheet.getCell(updatedRow, dataStartCol + 1).value = group?.name || ''
      sheet.getCell(updatedRow, dataStartCol + 1).style = { ...nameStyleCenter } // copy to avoid overwriting

      // name
      const cellId = inspectionItem.inspection_item_id?.substring(inspectionItem.inspection_item_id.length - 8) || ''
      const name = inspectionItem.part_name || ''
      if (settings.sheet_diagram_visibility?.polyline_diagram) {
        sheet.getCell(updatedRow, dataStartCol + 2).value = {
          formula: `HYPERLINK("#'${DIAGRAM_SHEET_NAME}'!"&ADDRESS(CELL("row", diagram_${cellId}), CELL("col",diagram_${cellId})), "${name}")`,
          date1904: false,
        }
        sheet.getCell(updatedRow, dataStartCol + 2).style = { ...nameStyleCenter, ...underlineStyle } // copy to avoid overwriting
        sheet.getCell(updatedRow, dataStartCol + 2).name = `data_${cellId}`
      } else {
        sheet.getCell(updatedRow, dataStartCol + 2).value = name
        sheet.getCell(updatedRow, dataStartCol + 2).style = { ...nameStyleCenter } // copy to avoid overwriting
      }

      // type
      sheet.getCell(updatedRow, dataStartCol + 3).value = '延長'
      sheet.getCell(updatedRow, dataStartCol + 3).style = { ...nameStyleCenter } // copy to avoid overwriting

      // Polyline has its own dedicated column block ('延長' parent): the length occupies the
      // threshold columns (実測値/設計値/規格値/差) and a single 幅 (width) column follows.
      const polylineStartCol = getStartColForCategory(colCountPerMetric, dataStartCol + 4, 'polyline_length')
      const polylineColCount = colCountPerMetric.find(([key]) => key === 'polyline_length')?.[1] ?? 0

      // A polyline row only uses its own block, so grey out every other metric column.
      const polylineEndCol = polylineStartCol + polylineColCount
      for (let i = dataStartCol + 4; i < totalCols; i += 1) {
        if (i < polylineStartCol || i >= polylineEndCol) {
          sheet.getCell(updatedRow, i).style = { ...nameStyle } // copy to avoid overwriting
        }
      }

      // 延長 length; the returned column is the single 幅 column that follows the threshold set.
      const widthCol = setCategoryValues(
        sheet,
        updatedRow,
        polylineStartCol,
        inspectionItem.polyline_length?.estimated_value || 0,
        inspectionItem,
        settings,
        settings.sheet_rows_tolerance_type?.polyline_length || DEFAULT_TOLERANCE_TYPE,
        meterToMilimeterRounded,
        valueStyleMili,
        plusMinusValueStyleMili,
      )

      // 幅 (width) is a single value in its own column
      const polylineWidth = inspectionItems.find(
        (item) =>
          item.item_type === InspectionItemType.PolylineWidth &&
          item.polyline_length_id === inspectionItem.inspection_item_id,
      )
      const widthValue = polylineWidth?.polyline_width?.estimated_value
      if (widthValue) {
        sheet.getCell(updatedRow, widthCol).value = widthValue
        sheet.getCell(updatedRow, widthCol).style = { ...valueStyleMili } // copy to avoid overwriting
      }

      updatedRow += 1
    }

    // custom measurements
    else if (
      settings.sheet_models_visibility?.length_with_distance_tool &&
      inspectionItem.item_type === InspectionItemType.LengthWithDistanceTool
    ) {
      // group
      const group = inspectionGroups.find((gr) => inspectionItem.inspection_group_id === gr.group_id)
      sheet.getCell(updatedRow, dataStartCol + 1).value = group?.name || ''
      sheet.getCell(updatedRow, dataStartCol + 1).style = { ...nameStyleCenter } // copy to avoid overwriting

      // name
      sheet.getCell(updatedRow, dataStartCol + 2).value = inspectionItem.part_name || ''
      sheet.getCell(updatedRow, dataStartCol + 2).style = { ...nameStyleCenter } // copy to avoid overwriting

      // type
      sheet.getCell(updatedRow, dataStartCol + 3).value = '距離'
      sheet.getCell(updatedRow, dataStartCol + 3).style = { ...nameStyleCenter } // copy to avoid overwriting

      // Custom measurement has its own dedicated '距離' column block; grey out every other
      // metric column, then write the value into that block.
      const distanceStartCol = getStartColForCategory(colCountPerMetric, dataStartCol + 4, 'length_with_distance_tool')
      const distanceColCount = colCountPerMetric.find(([key]) => key === 'length_with_distance_tool')?.[1] ?? 0
      const distanceEndCol = distanceStartCol + distanceColCount
      for (let i = dataStartCol + 4; i < totalCols; i += 1) {
        if (i < distanceStartCol || i >= distanceEndCol) {
          sheet.getCell(updatedRow, i).style = { ...nameStyle } // copy to avoid overwriting
        }
      }

      setCategoryValues(
        sheet,
        updatedRow,
        distanceStartCol,
        inspectionItem.length_with_distance_tool?.estimated_value || 0,
        inspectionItem,
        settings,
        settings.sheet_rows_tolerance_type?.length_with_distance_tool || DEFAULT_TOLERANCE_TYPE,
        meterToMilimeterRounded,
        valueStyleMili,
        plusMinusValueStyleMili,
      )

      updatedRow += 1
    }
  }

  const allItems = [...volumes, ...standaloneShapes, ...polylines, ...customMeasurement].map<{
    group_id?: string
    order?: number
    item: InspectionItem
  }>((item) => {
    // Surface plane group info is stored in the polygon shape
    if (item.item_type === InspectionItemType.PolygonArea) {
      const polygon = shapes.polygons?.find((shape) => shape.shape_id === item.shape_ids.polygons?.[0])
      return {
        group_id: polygon?.inspection_group_id,
        order: polygon?.order_in_inspection_group,
        item,
      }
    }

    return {
      group_id: item.inspection_group_id,
      order: item.order_in_inspection_group,
      item,
    }
  })

  if (settings.sheet_order_type_of_items === ProjectSheetItemsOrder.CustomOrder) {
    // Order groups and add set items of each group
    inspectionGroups
      .sort((a, b) => a.order - b.order)
      .forEach((group) =>
        allItems
          .filter((item) => item?.group_id === group.group_id)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .forEach((item) => setRow(item.item)),
      )

    // Add non-grouped items
    allItems.filter(({ group_id }) => !group_id).forEach((item) => setRow(item.item))
  } else {
    allItems.forEach((item) => setRow(item.item))
  }

  // end row for merge depends on whether there's data or not for this inspecteion area.
  // if there's data, we started with current row (updatedRow) minus 1 as updatedRow is already incremented for new data
  let endRow = updatedRow - 1
  let offset = 0
  if (startingRow === updatedRow) {
    endRow = startingRow
    offset = 1 // we need to increment the row for the next inspection area
  }

  // set inspection area name, only for project-level
  // エリア名
  sheet.mergeCells(startingRow, beginningCol, endRow, beginningCol)
  sheet.getCell(startingRow, beginningCol).value = {
    text: inspectionArea.inspection_area_name,
    hyperlink: `${HATSULY_URL}/projects/${project.project_id}/editor?area=${inspectionArea.inspection_area_id}`,
  }
  sheet.getCell(startRow, beginningCol).style = { ...nameStyle, ...underlineStyle } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol, startRow], [beginningCol, endRow])

  // 工種名
  sheet.mergeCells(startingRow, beginningCol + 1, endRow, beginningCol + 1)
  sheet.getCell(startingRow, beginningCol + 1).value = inspectionSheet.construction_properties?.construction_type || '-'
  sheet.getCell(startRow, beginningCol + 1).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 1, startRow], [beginningCol + 1, endRow])

  // 種別
  sheet.mergeCells(startingRow, beginningCol + 2, endRow, beginningCol + 2)
  sheet.getCell(startingRow, beginningCol + 2).value =
    inspectionSheet.construction_properties?.construction_type_detailed || '-'
  sheet.getCell(startRow, beginningCol + 2).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 2, startRow], [beginningCol + 2, endRow])

  // 作成者
  sheet.mergeCells(startingRow, beginningCol + 3, endRow, beginningCol + 3)
  sheet.getCell(startingRow, beginningCol + 3).value = inspectionSheet.creator_name || '-'
  sheet.getCell(startRow, beginningCol + 3).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 3, startRow], [beginningCol + 3, endRow])

  // 作成日
  sheet.mergeCells(startingRow, beginningCol + 4, endRow, beginningCol + 4)
  sheet.getCell(startingRow, beginningCol + 4).value = inspectionSheet?.create_time_user_specified
    ? dayjs(inspectionSheet.create_time_user_specified).format('YYYY/MM/DD')
    : '-'
  sheet.getCell(startRow, beginningCol + 4).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 4, startRow], [beginningCol + 4, endRow])

  // 立会者
  sheet.mergeCells(startingRow, beginningCol + 5, endRow, beginningCol + 5)
  sheet.getCell(startingRow, beginningCol + 5).value = inspectionSheet.observer_name || ''
  sheet.getCell(startRow, beginningCol + 5).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 5, startRow], [beginningCol + 5, endRow])

  // 立会日
  sheet.mergeCells(startingRow, beginningCol + 6, endRow, beginningCol + 6)
  sheet.getCell(startingRow, beginningCol + 6).value = inspectionSheet?.observe_time_user_specified
    ? dayjs(inspectionSheet.observe_time_user_specified).format('YYYY/MM/DD')
    : ''
  sheet.getCell(startRow, beginningCol + 6).style = { ...nameStyleCenter } // copy to avoid overwriting
  createOuterBorder(sheet, [beginningCol + 6, startRow], [beginningCol + 6, endRow])

  // border around type and name columns
  createOuterBorder(sheet, [dataStartCol + 1, startRow], [dataStartCol + 3, endRow])

  // border around data columns
  let i = dataStartCol + 4
  colCountPerMetric.forEach(([, count]) => {
    createOuterBorder(sheet, [i, startRow], [i + count - 1, endRow])
    i += count
  })

  // if there are no data, we still need to fill name/type columns
  if (volumes.length + standaloneShapes.length + customMeasurement.length + polylines.length === 0) {
    sheet.getCell(updatedRow, dataStartCol + 1).style = { ...nameStyle } // copy to avoid overwriting
    sheet.getCell(updatedRow, dataStartCol + 2).style = { ...nameStyle } // copy to avoid overwriting
    sheet.getCell(updatedRow, dataStartCol + 3).style = { ...nameStyle } // copy to avoid overwriting
    createOuterBorder(sheet, [dataStartCol + 1, updatedRow], [dataStartCol + 3, updatedRow])
  }

  return updatedRow + offset
}

/**
 * Add the data sheet to the workbook.
 *
 * @param workbook Workbook
 * @param project Current project
 * @param inspectionAreas All inspection areas of the project
 * @param inspectionSheet Inspection sheet
 * @param inspectionItems All inspection items of the project
 * @param shapes All shapes of the project
 */
export const addDataSheet = (
  workbook: Workbook,
  project: Project,
  inspectionAreas: InspectionArea[],
  inspectionSheets: InspectionSheet[],
  inspectionItems: InspectionItem[],
  inspectionGroups: InspectionGroup[],
  shapes: Shapes,
  forProject = false,
) => {
  let inspectionSheet = findInspectionAreaSheet(inspectionSheets, inspectionAreas[0].inspection_area_id)
  const sheet = workbook.addWorksheet(DATA_SHEET_NAME, {
    pageSetup: { orientation: 'landscape', paperSize: 9 },
  })

  // start from 1st row
  let bodyRow = 1

  // ## worksheet title
  bodyRow = setTitle(sheet, bodyRow, DATA_SHEET_NAME) + 1 // +1 for padding

  // ## profile section
  bodyRow = setProfileGroup(sheet, bodyRow, project, inspectionAreas[0], inspectionSheet, forProject) + 1 // +1 for padding

  // ## Summary
  // inspection sheet is only available for inspection area export, hence the check below
  bodyRow = setSummary(sheet, inspectionItems, shapes, bodyRow) + 1 // +1 for padding

  // ## Data table
  const [newRow, colCountPerMetric, totalCols] = setTableHeaders(sheet, bodyRow, project, inspectionItems, forProject)
  bodyRow = newRow

  // for every inspection area, set the values
  if (!forProject && inspectionSheet) {
    setInspectionAreaRow(
      sheet,
      bodyRow,
      colCountPerMetric,
      totalCols,
      project,
      inspectionAreas[0],
      inspectionSheet,
      inspectionItems,
      inspectionGroups,
      shapes,
      project,
      forProject,
    )
  } else {
    inspectionAreas.forEach((inspectionArea) => {
      inspectionSheet = findInspectionAreaSheet(inspectionSheets, inspectionArea.inspection_area_id)
      if (!inspectionSheet) return

      const items = inspectionItems.filter(
        (item) =>
          parseInspectionItemPartitionKey(item.partition_key!).inspectionAreaId === inspectionArea.inspection_area_id,
      )
      bodyRow = setInspectionAreaRow(
        sheet,
        bodyRow,
        colCountPerMetric,
        totalCols,
        project,
        inspectionArea,
        inspectionSheet,
        items,
        inspectionGroups.filter((group) => group.inspection_area_id === inspectionArea.inspection_area_id),
        shapes,
        project,
        forProject,
      )
    })
  }

  // If using creation date as order, hide the group column (H)
  if (project.sheet_order_type_of_items === ProjectSheetItemsOrder.CreatedAt) {
    sheet.getColumn('H').hidden = true
  }

  fitColumns(sheet)
  sheet.eachRow((row, index) => {
    if (index > 9) {
      row.height = 25
    }
  })
}
